import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { MeasurementUnit, UNITS, getUnit } from "@brewbase/lib";
import React, { useMemo } from "react";

interface UnitSelectProps {
  allowedBaseUnits?: string[];
  placeholder?: string;
  onlyBaseUnits?: boolean;
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export default function UnitSelect({
  allowedBaseUnits,
  placeholder,
  value,
  onChange,
  onlyBaseUnits,
  className,
}: UnitSelectProps) {
  const groups = useMemo(() => {
    const unitsByBaseSymbol = new Map<string, MeasurementUnit[]>();

    // Group units by their base symbol
    Array.from(UNITS.values()).forEach((unit) => {
      const baseSymbol = unit.base_unit_symbol;
      if (allowedBaseUnits && !allowedBaseUnits.includes(baseSymbol)) {
        return;
      }
      if (onlyBaseUnits && baseSymbol !== unit.symbol) {
        return;
      }
      if (!unitsByBaseSymbol.has(baseSymbol)) {
        unitsByBaseSymbol.set(baseSymbol, []);
      }
      unitsByBaseSymbol.get(baseSymbol)?.push(unit);
    });

    // Convert to options array with base symbol as group label
    return Array.from(unitsByBaseSymbol.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([baseSymbol, units]) => ({
        group: getUnit(baseSymbol)!.dimension_name,
        options: units.map((u) => ({
          value: u.symbol,
          label: u.display_name
            ? `${u.display_symbol ?? u.symbol} (${u.display_name})`
            : (u.display_symbol ?? u.symbol),
        })),
      }));
  }, [allowedBaseUnits]);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn("w-max min-w-0 max-w-[200px]", className)}>
        <SelectValue
          placeholder={placeholder}
          className="text-left"
        ></SelectValue>
      </SelectTrigger>
      <SelectContent>
        {groups.map((group, idx) => (
          <React.Fragment key={group.group}>
            <SelectGroup>
              {groups.length > 1 && <SelectLabel>{group.group}</SelectLabel>}
              {group.options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
            {idx < groups.length - 1 && <SelectSeparator />}
          </React.Fragment>
        ))}
      </SelectContent>
    </Select>
  );
}
